import { parseAdkEvent } from '../adkEventParser';
import { ADK_EVENT_TYPES } from '../constants';

/**
 * Merges arrays while preventing duplicates based on a unique key.
 * Needed because streaming events may send the same components/vulnerabilities
 * multiple times as data arrives in chunks. This ensures each item appears only once.
 */
function mergeUniqueByKey(existing, newItems, getKey) {
  if (!existing || existing.length === 0) {
    return newItems || [];
  }
  if (!newItems || newItems.length === 0) {
    return existing;
  }
  
  const existingKeys = new Set(existing.map(getKey));
  const uniqueNewItems = newItems.filter(item => !existingKeys.has(getKey(item)));
  return [...existing, ...uniqueNewItems];
}

function getComponentKey(component) {
  return `${component.name}-${component.version}-${component.vendor || ''}`;
}

function getVulnerabilityKey(vulnerability) {
  return vulnerability.vulnerability_name || 
         vulnerability['Vulnerability Name'] || 
         vulnerability.vulnerabilityName || 
         vulnerability.name || 
         vulnerability.cve || 
         '';
}

function handleStructuredData(message, event) {
  const updated = { ...message };
  
  if (event.components && Array.isArray(event.components)) {
    updated.components = mergeUniqueByKey(
      message.components,
      event.components,
      getComponentKey
    );
  }
  
  if (event.vulnerabilities && Array.isArray(event.vulnerabilities)) {
    updated.vulnerabilities = mergeUniqueByKey(
      message.vulnerabilities,
      event.vulnerabilities,
      getVulnerabilityKey
    );
  }
  
  if (event.actionButtons && Array.isArray(event.actionButtons)) {
    updated.actionButtons = event.actionButtons;
  }
  
  return updated;
}

/**
 * Custom hook to handle SSE streaming and event processing
 * Returns event handlers for ADK events
 */
export function useChatStream({
  setMessages,
  allEventsRef,
  processedChunkIds,
  lastProcessedInvocationId,
  onAuthorChange
}) {
  const handleAdkEvent = event => {
    switch (event.type) {
      case ADK_EVENT_TYPES.TEXT_MESSAGE_START:
        setMessages(prev => {
          // Check if a message with this messageId already exists
          if (event.messageId) {
            const existingMessage = prev.find(
              msg => msg.messageId === event.messageId && msg.role === 'assistant'
            );
            if (existingMessage) {
              // Message already exists, don't create a duplicate
              return prev;
            }
          }
          
          // Create new message
          return [
            ...prev,
            {
              role: 'assistant',
              content: '',
              messageId: event.messageId, // Store messageId to match with content events
              eventId: event.eventId, // Store eventId from raw data for feedback
              timestamp: new Date()
            }
          ];
        });
        if (event.rawEvent?.invocationId) {
          lastProcessedInvocationId.current = event.rawEvent.invocationId;
        }
        break;

      case ADK_EVENT_TYPES.TEXT_MESSAGE_CONTENT:
        setMessages(prev => {
          const newMessages = [...prev];
          
          // Find the message with matching messageId
          let targetMessage = null;
          if (event.messageId) {
            // Find message by messageId (search from end to find most recent match)
            targetMessage = newMessages.findLast(
              msg => msg.messageId === event.messageId && msg.role === 'assistant'
            ) || null;
          }
          
          // If no matching message found and we have a messageId, create a new message
          // This handles cases where TEXT_MESSAGE_CONTENT arrives before TEXT_MESSAGE_START
          if (!targetMessage && event.messageId) {
            const newMessage = {
              role: 'assistant',
              content: '',
              messageId: event.messageId,
              eventId: event.eventId, // Store eventId from raw data for feedback
              timestamp: new Date(event.timestamp || Date.now())
            };
            newMessages.push(newMessage);
            targetMessage = newMessage;
          }
          
          // Update eventId if it's provided and not already set (in case content arrives before start)
          if (event.eventId && targetMessage && !targetMessage.eventId) {
            targetMessage.eventId = event.eventId;
          }
          
          // Fallback to last assistant message only if no messageId was provided (backward compatibility)
          if (!targetMessage) {
            targetMessage = newMessages[newMessages.length - 1];
          }
          
          if (!targetMessage || targetMessage.role !== 'assistant') {
            return newMessages;
          }
          
          const hasStructuredData = !!(event.components || event.vulnerabilities || event.actionButtons);
          
          // Handle structured data updates (components/vulnerabilities/actionButtons)
          if (hasStructuredData) {
            const updated = handleStructuredData(targetMessage, event);
            Object.assign(targetMessage, updated);
          }
          
          // Process text content - allow it even if message has components/vulnerabilities
          // This allows presenter to show both text and tables
          if (event.content) {
            const newContent = event.content || '';
            const currentContent = targetMessage.content || '';
            targetMessage.content = event.isFullContent ? newContent : currentContent + newContent;
          }
          
          return newMessages;
        });
        break;

      case ADK_EVENT_TYPES.TEXT_MESSAGE_END:
        break;

      case ADK_EVENT_TYPES.TOOL_CALL_START:
      case ADK_EVENT_TYPES.TOOL_CALL_RESULT:
      case ADK_EVENT_TYPES.TOOL_CALL_END:
        break;

      case ADK_EVENT_TYPES.ERROR:
        // Handle error events from the stream
        console.log('useChatStream: Received ERROR event:', event);
        const errorMessage = 'Sorry, I encountered an error. Please try again.';
        console.log('useChatStream: Formatted error message:', errorMessage);
        
        // Determine if this error is retryable
        const isRetryable = event.isPartialResponse || 
                           event.isConnectionLost || 
                           event.isEmptyToolResponse || 
                           event.isMalformedResponse ||
                           event.isStreamError ||
                           event.isStreamProcessingError;
        
        setMessages(prev => {
          if (prev.length === 0) {
            return [{
              role: 'assistant',
              content: errorMessage,
              error: true,
              retryable: isRetryable,
              errorCode: event.errorCode,
              timestamp: new Date(event.timestamp)
            }];
          }
          
          const lastMessage = prev[prev.length - 1];
          if (lastMessage.role === 'assistant') {
            // Update the last assistant message with error
            return [
              ...prev.slice(0, -1),
              {
                ...lastMessage,
                content: errorMessage,
                error: true,
                retryable: isRetryable,
                errorCode: event.errorCode,
                timestamp: new Date(event.timestamp)
              }
            ];
          }
          
          // Create new error message if no assistant message exists
          return [
            ...prev,
            {
              role: 'assistant',
              content: errorMessage,
              error: true,
              retryable: isRetryable,
              errorCode: event.errorCode,
              timestamp: new Date(event.timestamp)
            }
          ];
        });
        break;

      case ADK_EVENT_TYPES.RUN_FINISHED:
        break;

      case ADK_EVENT_TYPES.UNKNOWN:
        if (process.env.NODE_ENV === 'development') {
          console.debug('Unknown ADK event format:', event.rawEvent);
        }
        break;
    }
  };

  const processSSEEvent = rawData => {
    // Track all events for debugging
    allEventsRef.current.push({
      timestamp: Date.now(),
      data: rawData,
      keys: Object.keys(rawData)
    });

    // Track author changes for spinner text
    if (rawData.author && onAuthorChange) {
      onAuthorChange(rawData.author);
    }

    // Parse as ADK event
    const adkEvents = parseAdkEvent(rawData, processedChunkIds.current, {
      lastProcessedInvocationId
    });

    // Handle normalized ADK events
    for (const event of adkEvents) {
      handleAdkEvent(event);
    }
  };

  return { processSSEEvent };
}
